/* eslint-disable @typescript-eslint/naming-convention -- operator symbols are the keys of the operator table */
/**
 * Evaluator for App Studio overlay expressions.
 *
 * The grammar, from the overlay contract: literals, attribute dot-paths (own properties
 * only, with an optional `@formatted` suffix), `+ - * / %`, comparisons, `&& || !`, the
 * ternary, and a closed set of functions — `sum(dataset, 'expr')`, `count(dataset)`,
 * `round`, `min`, `max`, `coalesce`, `if`, `formatCurrency`, `isOffline()` (contract v1.9).
 *
 * A hand-written recursive-descent parser rather than `new Function`: configuration is
 * authored outside this codebase, and the only way an expression can reach code is through
 * the whitelist above. Nothing here can call into the page, the DOM or a service.
 *
 * Pure and framework-free. Never throws on a bad expression: `evaluate` returns `undefined`
 * and warns once per expression, because a typo in a tenant's config must not take a screen
 * down. See docs/features/order-capture.md.
 */

import { roundHalfAwayFromZero } from '@omni/utility/round.util';

/** What an expression reads. Built by the page with `buildRecordScope`. */
export interface AppUiScope {
  /** A dot-path such as `orderMatrix.lineCount` or `indskr_ordertype@formatted`. */
  get(path: string): unknown;
  /** Rows for `count()` and `sum()`, by dataset name. */
  dataset(name: string): unknown[];
  formatCurrency(value: number): string;
  /** Resolves an i18n key, for a built-in fallback's templates. Absent means keys pass through. */
  translate?(key: string): string;
  /** True while the device has no connection. Absent means online. */
  isOffline?(): boolean;
}

type Token =
  | { kind: 'num'; value: number }
  | { kind: 'str'; value: string }
  | { kind: 'id'; value: string }
  | { kind: 'op'; value: string }
  | { kind: 'end' };

type Ast =
  | { kind: 'lit'; value: unknown }
  | { kind: 'path'; path: string }
  | { kind: 'unary'; op: string; arg: Ast }
  | { kind: 'binary'; op: string; left: Ast; right: Ast }
  | { kind: 'ternary'; test: Ast; then: Ast; otherwise: Ast }
  | { kind: 'call'; name: string; args: Ast[] };

const TWO_CHAR_OPS = ['==', '!=', '<=', '>=', '&&', '||'];
const ONE_CHAR_OPS = [
  '+',
  '-',
  '*',
  '/',
  '%',
  '<',
  '>',
  '!',
  '?',
  ':',
  '(',
  ')',
  ',',
];

class ExpressionError extends Error {}

// ---------------------------------------------------------------------------------------
// tokenizer
// ---------------------------------------------------------------------------------------

const NUMBER = /^[0-9]*\.?[0-9]+(?:e[+-]?[0-9]+)?/i;
/** Dot-paths with an optional `@formatted` suffix on any segment. */
const IDENTIFIER =
  /^[A-Za-z_][A-Za-z0-9_]*(?:@[A-Za-z]+)?(?:\.[A-Za-z_][A-Za-z0-9_]*(?:@[A-Za-z]+)?)*/;

function tokenize(source: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;
  while (i < source.length) {
    if (/\s/.test(source[i])) {
      i++;
      continue;
    }
    const [token, length] = readToken(source, i);
    tokens.push(token);
    i += length;
  }
  tokens.push({ kind: 'end' });
  return tokens;
}

/** The token starting at `i`, and how many characters it consumed. */
function readToken(source: string, i: number): [Token, number] {
  const ch = source[i];
  const rest = source.slice(i);

  if (/[0-9]/.test(ch) || (ch === '.' && /[0-9]/.test(source[i + 1] ?? ''))) {
    const match = NUMBER.exec(rest);
    if (!match) throw new ExpressionError(`bad number at ${i}`);
    return [{ kind: 'num', value: Number(match[0]) }, match[0].length];
  }
  if (ch === "'" || ch === '"') {
    const end = source.indexOf(ch, i + 1);
    if (end < 0) throw new ExpressionError(`unterminated string at ${i}`);
    return [{ kind: 'str', value: source.slice(i + 1, end) }, end + 1 - i];
  }
  if (/[A-Za-z_]/.test(ch)) {
    const match = IDENTIFIER.exec(rest);
    if (!match) throw new ExpressionError(`bad identifier at ${i}`);
    return [{ kind: 'id', value: match[0] }, match[0].length];
  }
  const two = rest.slice(0, 2);
  if (TWO_CHAR_OPS.includes(two)) return [{ kind: 'op', value: two }, 2];
  if (ONE_CHAR_OPS.includes(ch)) return [{ kind: 'op', value: ch }, 1];
  throw new ExpressionError(`unexpected "${ch}" at ${i}`);
}

// ---------------------------------------------------------------------------------------
// parser — precedence climbing, lowest first
// ---------------------------------------------------------------------------------------

class Parser {
  private pos = 0;

  constructor(private readonly tokens: Token[]) {}

  public parse(): Ast {
    const ast = this.ternary();
    if (this.peek().kind !== 'end')
      throw new ExpressionError('unexpected trailing input');
    return ast;
  }

  private peek(): Token {
    return this.tokens[this.pos];
  }

  private isOp(value: string): boolean {
    const token = this.peek();
    return token.kind === 'op' && token.value === value;
  }

  private takeOp(value: string): void {
    if (!this.isOp(value)) throw new ExpressionError(`expected "${value}"`);
    this.pos++;
  }

  private ternary(): Ast {
    const test = this.or();
    if (!this.isOp('?')) return test;
    this.pos++;
    const then = this.ternary();
    this.takeOp(':');
    const otherwise = this.ternary();
    return { kind: 'ternary', test, then, otherwise };
  }

  private or(): Ast {
    return this.binaryChain(() => this.and(), ['||']);
  }

  private and(): Ast {
    return this.binaryChain(() => this.equality(), ['&&']);
  }

  private equality(): Ast {
    return this.binaryChain(() => this.relational(), ['==', '!=']);
  }

  private relational(): Ast {
    return this.binaryChain(() => this.additive(), ['<', '<=', '>', '>=']);
  }

  private additive(): Ast {
    return this.binaryChain(() => this.multiplicative(), ['+', '-']);
  }

  private multiplicative(): Ast {
    return this.binaryChain(() => this.unary(), ['*', '/', '%']);
  }

  private binaryChain(next: () => Ast, ops: string[]): Ast {
    let left = next();
    while (
      this.peek().kind === 'op' &&
      ops.includes((this.peek() as { value: string }).value)
    ) {
      const op = (this.peek() as { value: string }).value;
      this.pos++;
      left = { kind: 'binary', op, left, right: next() };
    }
    return left;
  }

  private unary(): Ast {
    if (this.isOp('!') || this.isOp('-')) {
      const op = (this.peek() as { value: string }).value;
      this.pos++;
      return { kind: 'unary', op, arg: this.unary() };
    }
    return this.primary();
  }

  private primary(): Ast {
    const token = this.peek();
    this.pos++;
    switch (token.kind) {
      case 'num':
        return { kind: 'lit', value: token.value };
      case 'str':
        return { kind: 'lit', value: token.value };
      case 'id': {
        if (token.value === 'true') return { kind: 'lit', value: true };
        if (token.value === 'false') return { kind: 'lit', value: false };
        if (token.value === 'null') return { kind: 'lit', value: null };
        if (this.isOp('(')) return this.call(token.value);
        return { kind: 'path', path: token.value };
      }
      case 'op':
        if (token.value === '(') {
          const inner = this.ternary();
          this.takeOp(')');
          return inner;
        }
        throw new ExpressionError(`unexpected "${token.value}"`);
      default:
        throw new ExpressionError('unexpected end of expression');
    }
  }

  private call(name: string): Ast {
    this.takeOp('(');
    const args: Ast[] = [];
    if (!this.isOp(')')) {
      args.push(this.ternary());
      while (this.isOp(',')) {
        this.pos++;
        args.push(this.ternary());
      }
    }
    this.takeOp(')');
    return { kind: 'call', name, args };
  }
}

// ---------------------------------------------------------------------------------------
// evaluation
// ---------------------------------------------------------------------------------------

function toNumber(value: unknown): number {
  if (typeof value === 'number') return value;
  if (typeof value === 'boolean') return value ? 1 : 0;
  if (value === null || value === undefined || value === '') return 0;
  const asNumber = Number(value);
  return Number.isNaN(asNumber) ? 0 : asNumber;
}

/** Null, undefined or the empty string: what a form treats as "not filled". */
export function isBlank(value: unknown): value is null | undefined | '' {
  return value === null || value === undefined || value === '';
}

/** Rounds half away from zero at `places`, immune to binary noise such as 2.135. */
export function roundTo(value: number, places: number): number {
  return roundHalfAwayFromZero(value, places);
}

/**
 * Loose equality as configuration expects it: `statuscode == 1` must hold whether the
 * device holds the number or its string, and a blank never equals a value.
 */
function looseEquals(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (isBlank(a) || isBlank(b)) return isBlank(a) && isBlank(b);
  if (typeof a === 'boolean' || typeof b === 'boolean')
    return asBoolean(a) === asBoolean(b);
  return String(a) === String(b);
}

/** A yes/no as older data holds it too: the string `"false"` or `"0"` is not true. */
function asBoolean(value: unknown): boolean {
  if (typeof value === 'string') {
    const text = value.trim().toLowerCase();
    return text !== 'false' && text !== '0' && text !== '';
  }
  return Boolean(value);
}

function evaluateAst(ast: Ast, scope: AppUiScope): unknown {
  switch (ast.kind) {
    case 'lit':
      return ast.value;
    case 'path':
      return scope.get(ast.path);
    case 'unary': {
      const arg = evaluateAst(ast.arg, scope);
      return ast.op === '!' ? !arg : -toNumber(arg);
    }
    case 'ternary':
      return evaluateAst(ast.test, scope)
        ? evaluateAst(ast.then, scope)
        : evaluateAst(ast.otherwise, scope);
    case 'binary':
      return evaluateBinary(ast, scope);
    case 'call':
      return evaluateCall(ast, scope);
    default:
      return undefined;
  }
}

function evaluateBinary(
  ast: Extract<Ast, { kind: 'binary' }>,
  scope: AppUiScope,
): unknown {
  // Short-circuit so the right side of `a && b` is never read when `a` decides it.
  if (ast.op === '&&')
    return evaluateAst(ast.left, scope) ? evaluateAst(ast.right, scope) : false;
  if (ast.op === '||') {
    const left = evaluateAst(ast.left, scope);
    return left ? left : evaluateAst(ast.right, scope);
  }

  const operator = BINARY_OPERATORS[ast.op];
  if (!operator) throw new ExpressionError(`unknown operator "${ast.op}"`);
  return operator(evaluateAst(ast.left, scope), evaluateAst(ast.right, scope));
}

/** Numbers numerically; two strings that are not numbers as text, so ISO dates order correctly. */
function compare(a: unknown, b: unknown): number {
  const isText = (value: unknown) =>
    typeof value === 'string' &&
    value.trim() !== '' &&
    Number.isNaN(Number(value));
  if (isText(a) && isText(b))
    return String(a) < String(b) ? -1 : String(a) > String(b) ? 1 : 0;
  return toNumber(a) - toNumber(b);
}

const BINARY_OPERATORS: Record<
  string,
  (left: unknown, right: unknown) => unknown
> = {
  '==': (a, b) => looseEquals(a, b),
  '!=': (a, b) => !looseEquals(a, b),
  '<': (a, b) => compare(a, b) < 0,
  '<=': (a, b) => compare(a, b) <= 0,
  '>': (a, b) => compare(a, b) > 0,
  '>=': (a, b) => compare(a, b) >= 0,
  // `+` concatenates when either side is text — `type@formatted + ' · ' + terms@formatted`.
  '+': (a, b) =>
    typeof a === 'string' || typeof b === 'string'
      ? `${a ?? ''}${b ?? ''}`
      : toNumber(a) + toNumber(b),
  '-': (a, b) => toNumber(a) - toNumber(b),
  '*': (a, b) => toNumber(a) * toNumber(b),
  // Division by zero yields zero rather than Infinity: a guard reading it must fail closed.
  '/': (a, b) => (toNumber(b) === 0 ? 0 : toNumber(a) / toNumber(b)),
  '%': (a, b) => (toNumber(b) === 0 ? 0 : toNumber(a) % toNumber(b)),
};

/** Arguments of a call, evaluated lazily so `if(c, a, b)` only reads the branch it takes. */
interface CallArgs {
  count: number;
  scope: AppUiScope;
  value(index: number): unknown;
  dataset(index: number): unknown[];
}

const FUNCTIONS: Record<string, (args: CallArgs) => unknown> = {
  count: (args) => args.dataset(0).length,
  // `sum(dataset, 'expr')` evaluates `expr` per row with the row's own properties in scope.
  sum: (args) => {
    const expression = String(args.value(1) ?? '');
    const rowAst = expression ? compile(expression) : null;
    return args
      .dataset(0)
      .reduce<number>(
        (total, row) =>
          total +
          toNumber(
            rowAst ? evaluateAst(rowAst, rowScopeFor(row, args.scope)) : row,
          ),
        0,
      );
  },
  round: (args) => roundTo(toNumber(args.value(0)), toNumber(args.value(1))),
  min: (args) =>
    Math.min(
      ...Array.from({ length: args.count }, (_, index) =>
        toNumber(args.value(index)),
      ),
    ),
  max: (args) =>
    Math.max(
      ...Array.from({ length: args.count }, (_, index) =>
        toNumber(args.value(index)),
      ),
    ),
  coalesce: (args) => {
    for (let index = 0; index < args.count; index++) {
      const candidate = args.value(index);
      if (!isBlank(candidate)) return candidate;
    }
    return null;
  },
  if: (args) => (args.value(0) ? args.value(1) : args.value(2)),
  // Text formulas (contract v1.6): blanks are skipped, so a missing part leaves no stray separator.
  concat: (args) => texts(args, 0).join(''),
  join: (args) => texts(args, 1).join(String(args.value(0) ?? '')),
  formatCurrency: (args) => args.scope.formatCurrency(toNumber(args.value(0))),
  isOffline: (args) => !!args.scope.isOffline?.(),
  translate: (args) => {
    const key = String(args.value(0) ?? '');
    return args.scope.translate ? args.scope.translate(key) : key;
  },
};

function texts(args: CallArgs, from: number): string[] {
  return Array.from({ length: Math.max(0, args.count - from) }, (_, index) =>
    args.value(index + from),
  )
    .filter((value) => !isBlank(value))
    .map(String);
}

function evaluateCall(
  ast: Extract<Ast, { kind: 'call' }>,
  scope: AppUiScope,
): unknown {
  // Own names only: `constructor` or `toString` must not resolve through the prototype.
  const fn = Object.prototype.hasOwnProperty.call(FUNCTIONS, ast.name)
    ? FUNCTIONS[ast.name]
    : undefined;
  if (!fn) throw new ExpressionError(`unknown function "${ast.name}"`);
  const value = (index: number): unknown =>
    ast.args[index] ? evaluateAst(ast.args[index], scope) : undefined;
  return fn({
    count: ast.args.length,
    value,
    // `count(orderLines)` names the dataset bare; `count('orderLines')` quotes it. Both work.
    dataset: (index) =>
      scope.dataset(
        ast.args[index]?.kind === 'path'
          ? (ast.args[index] as { path: string }).path
          : String(value(index) ?? ''),
      ),
    scope,
  });
}

/** A row's own properties first, then the outer scope. */
function rowScopeFor(row: unknown, outer: AppUiScope): AppUiScope {
  const record =
    row && typeof row === 'object' ? (row as Record<string, unknown>) : {};
  const translate = outer.translate;
  return {
    get: (path) => {
      const head = path.split('.')[0].split('@')[0];
      return Object.prototype.hasOwnProperty.call(record, head)
        ? readPath(record, path)
        : outer.get(path);
    },
    dataset: (name) => outer.dataset(name),
    formatCurrency: (value) => outer.formatCurrency(value),
    translate: translate ? (key) => translate(key) : undefined,
    isOffline: () => !!outer.isOffline?.(),
  };
}

/**
 * Reads a dot-path off a plain object, own properties only. A `@formatted` suffix looks for
 * `<name>@formatted` first and falls back to the raw value.
 */
export function readPath(
  record: Record<string, unknown>,
  path: string,
): unknown {
  let current: unknown = record;
  for (const segment of path.split('.')) {
    if (!current || typeof current !== 'object') return undefined;
    const object = current as Record<string, unknown>;
    if (Object.prototype.hasOwnProperty.call(object, segment)) {
      current = object[segment];
      continue;
    }
    const [name, suffix] = segment.split('@');
    if (suffix && Object.prototype.hasOwnProperty.call(object, name)) {
      current = object[name];
      continue;
    }
    return undefined;
  }
  return current;
}

// ---------------------------------------------------------------------------------------
// public surface
// ---------------------------------------------------------------------------------------

const parsed = new Map<string, Ast | null>();
const warned = new Set<string>();

/** Parses once per distinct expression; a bad one is remembered as unparseable. */
function compile(expression: string): Ast | null {
  if (parsed.has(expression)) return parsed.get(expression) ?? null;
  let ast: Ast | null = null;
  try {
    ast = new Parser(tokenize(expression)).parse();
  } catch (error) {
    warnOnce(expression, error);
  }
  parsed.set(expression, ast);
  return ast;
}

function warnOnce(expression: string, error: unknown): void {
  if (warned.has(expression)) return;
  warned.add(expression);
  console.warn(
    `app-ui: cannot evaluate expression "${expression}":`,
    error instanceof Error ? error.message : error,
  );
}

/**
 * As `evaluateExpression`, telling an expression that cannot be evaluated (bad syntax, unknown
 * function) from one whose value is undefined: a guard must not read the first as "passes".
 */
export function tryEvaluateExpression(
  expression: string,
  scope: AppUiScope,
): { ok: true; value: unknown } | { ok: false } {
  const ast = compile(expression);
  if (!ast) return { ok: false };
  try {
    return { ok: true, value: evaluateAst(ast, scope) };
  } catch (error) {
    warnOnce(expression, error);
    return { ok: false };
  }
}

/** Evaluates `expression` against `scope`; `undefined` when it cannot be evaluated. */
export function evaluateExpression(
  expression: string,
  scope: AppUiScope,
): unknown {
  const ast = compile(expression);
  if (!ast) return undefined;
  try {
    return evaluateAst(ast, scope);
  } catch (error) {
    warnOnce(expression, error);
    return undefined;
  }
}

/** Evaluates `expression` with `row`'s own properties in front of `scope`. */
export function evaluateInRow(
  expression: string,
  row: unknown,
  scope: AppUiScope,
): unknown {
  return evaluateExpression(expression, rowScopeFor(row, scope));
}

/**
 * Renders a `{{expression}}` template against a row — the overlay's `titleTemplate`,
 * `subtitleTemplate` and `totalTemplate`. An expression that yields nothing renders empty.
 */
export function renderTemplate(
  template: string,
  row: unknown,
  scope: AppUiScope,
): string {
  return String(template ?? '').replace(
    /\{\{\s*([^}]+?)\s*\}\}/g,
    (_match, expression: string) => {
      const value = evaluateInRow(expression, row, scope);
      return value === null || value === undefined ? '' : String(value);
    },
  );
}

/** For tests only. */
export function clearExpressionCache(): void {
  parsed.clear();
  warned.clear();
}

/**
 * The scope a screen exposes to overlay expressions, built from what any record screen has:
 * captured values (raw and `@formatted`), lookups Dataverse spells `_x_value`, the feature's
 * computed figures under their own namespace, and named datasets.
 *
 * Pure: a function of its inputs, so the names an expression can read are provable from a
 * fixture rather than from a rendered screen.
 */
export interface AppUiRecordScopeInput {
  /** Captured values by attribute: `{ value, label }`. The label is the `@formatted` text. */
  values: Record<
    string,
    { value: string | number | boolean | null; label: string }
  >;
  /**
   * Lookups the overlay reads the Dataverse way — `_customerid_value` and its `@formatted`
   * twin. Keyed by attribute; each becomes both spellings.
   */
  lookups?: Record<string, { id: string | null; label: string }>;
  /** Feature figures, each under its own name, e.g. `{ orderMatrix: { lineCount, … } }`. */
  computed?: Record<string, unknown>;
  datasets?: Record<string, unknown[]>;
  formatCurrency: (value: number) => string;
  translate?: (key: string) => string;
  /** The device's connectivity, for `isOffline()`; read at evaluation time. */
  isOffline?: () => boolean;
}

export function buildRecordScope(input: AppUiRecordScopeInput): AppUiScope {
  const record: Record<string, unknown> = {};
  for (const [attribute, value] of Object.entries(input.values ?? {})) {
    record[attribute] = value.value;
    record[`${attribute}@formatted`] = value.label;
  }
  for (const [attribute, lookup] of Object.entries(input.lookups ?? {})) {
    record[`_${attribute}_value`] = lookup.id;
    record[`_${attribute}_value@formatted`] = lookup.label;
  }
  Object.assign(record, input.computed ?? {});

  const datasets = input.datasets ?? {};
  return {
    get: (path) => readPath(record, path),
    dataset: (name) => datasets[name] ?? [],
    formatCurrency: input.formatCurrency,
    translate: input.translate,
    isOffline: input.isOffline,
  };
}
